import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useLiveQuery } from 'dexie-react-hooks';
import { ArrowRight, Calculator, Dumbbell, FolderOpen, History, ListPlus, Play, Search, Sparkles } from 'lucide-react';
import { PageHeader } from '@/components/PageHeader';
import { Card, Emblema, EstadoVacio, Pastilla } from '@/components/Card';
import { Banner } from '@/components/Banner';
import { Button, BotonEnlace } from '@/components/Button';
import { Fila, Filas } from '@/components/Fila';
import { Segmentado } from '@/components/Segmentado';
import { actualizarSettings, leerSettings } from '@/db/schema';
import { leerCambiosDeDescanso, leerRutinasEnLista, crearRutina } from '@/db/rutinas';
import { iniciarEntrenamiento, leerEnCurso, leerTerminados } from '@/db/entrenamientos';
import { mensajeDeError } from '@/db/tx';
import { nuevoId } from '@/db/ids';
import type { Modalidad } from '@/db/types';
import { CICLOS, esDeCiclo, plantillaDe } from '@/datos/rutinas-precargadas';
import { fechaCorta, textoEjercicios } from '@/domain/hoy';
import { formatearCorto, nombreDia } from '@/domain/dates';
import { horaLocalDe } from '@/domain/time';
import { propuestasDeGym, textoDias, type SesionPropuesta } from '@/domain/sesiones';
import { ETIQUETAS_MODALIDAD, MODALIDADES, modalidadDe } from '@/domain/modalidad';
import { areaDe, disponible, FASE_ACTUAL } from '@/app/areas';
import { useFechaHoy } from '@/lib/useFechaHoy';
import { TarjetaDescansos, TarjetaPrecargadas, TarjetaSigue } from './Precargadas';

const AREA = areaDe('/gym');

/**
 * Gym (Fase 2, diseño Foco), en la modalidad elegida (gimnasio o casa): la
 * sesión en curso, la que toca hoy, la que faltó y la que sigue en las
 * sesiones sin días fijos (solo se proponen: nada avanza ni se completa
 * solo), las rutinas de esa modalidad con la última vez, las rutinas
 * precargadas de esa modalidad y, en el gimnasio, sus descansos confirmados.
 */
export function GymPage() {
  const hoy = useFechaHoy();
  const navegar = useNavigate();
  const rutinas = useLiveQuery(() => leerRutinasEnLista(), []);
  const enCurso = useLiveQuery(() => leerEnCurso(), []);
  const historial = useLiveQuery(() => leerTerminados(), []);
  const ajustes = useLiveQuery(() => leerSettings(), []);
  const cambiosDescanso = useLiveQuery(() => leerCambiosDeDescanso(), []);
  const [ocupado, setOcupado] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [idRutinaNueva] = useState(() => nuevoId('rut'));

  if (
    rutinas === undefined ||
    enCurso === undefined ||
    historial === undefined ||
    ajustes === undefined ||
    cambiosDescanso === undefined
  ) {
    return null;
  }

  if (AREA !== undefined && !disponible(AREA)) {
    return (
      <div>
        <PageHeader compacto titulo="Tu entrenamiento" descripcion="Rutinas claras para concentrarte en entrenar." />
        <EstadoVacio icono={Dumbbell} titulo={`Llega en la Fase ${AREA.fase}`} descripcion={AREA.contenido}>
          <p className="text-[12px] text-texto-suave">Vas en la Fase {FASE_ACTUAL}.</p>
        </EstadoVacio>
      </div>
    );
  }

  // Elegir la modalidad solo cambia lo que se ve: cada una con sus rutinas y propuestas
  const modalidad = modalidadDe({ modalidad: ajustes.modalidadGym });
  const enGimnasio = modalidad === 'gimnasio';
  const persona = ajustes.rutinasDe ?? null;
  const deModalidad = rutinas.filter((r) => modalidadDe(r.rutina) === modalidad);
  // La de hoy, la que faltó y, en las sesiones sin días fijos (A, B y C de
  // Fernando), la que sigue; Hoy usa la misma función (0.3.7)
  const { propuestas, siguientes } = propuestasDeGym(
    rutinas.map((r) => r.rutina),
    historial.terminados,
    historial.conSeries,
    CICLOS,
    modalidad,
    hoy,
  );

  async function empezar(routineId: string | null) {
    if (ocupado) return;
    setOcupado(true);
    setError(null);
    try {
      await iniciarEntrenamiento(routineId, new Date(), modalidad);
      void navegar('/gym/entrenamiento');
    } catch (e) {
      setError(mensajeDeError(e));
      setOcupado(false);
    }
  }

  async function nuevaRutina() {
    setError(null);
    try {
      const id = await crearRutina('Rutina nueva', idRutinaNueva, modalidad);
      void navegar(`/gym/rutina/${id}?editar=1`);
    } catch (e) {
      setError(mensajeDeError(e));
    }
  }

  async function cambiarModalidad(m: Modalidad) {
    setError(null);
    try {
      await actualizarSettings({ modalidadGym: m });
    } catch (e) {
      setError(mensajeDeError(e));
    }
  }

  return (
    <div className="space-y-5">
      <PageHeader compacto titulo="Tu entrenamiento" descripcion="Rutinas claras para concentrarte en entrenar." />

      <Segmentado
        etiqueta="Modalidad"
        completo
        valor={modalidad}
        onCambio={(m) => void cambiarModalidad(m)}
        opciones={MODALIDADES.map((m) => ({ valor: m, etiqueta: ETIQUETAS_MODALIDAD[m] }))}
      />

      {error !== null && (
        <Banner tono="alerta" titulo="No se pudo">
          {error}
        </Banner>
      )}

      {enCurso !== null ? (
        <Card>
          <div className="flex items-start justify-between gap-3">
            <span className="antetitulo">Sesión en curso</span>
            <Pastilla>{ETIQUETAS_MODALIDAD[modalidadDe(enCurso)]}</Pastilla>
          </div>
          <h2 className="mb-1 mt-3 text-[22px] leading-tight tracking-[-0.6px] text-texto">{enCurso.nombre}</h2>
          <p className="mb-4 text-[13px] text-texto-suave">Desde las {horaLocalDe(enCurso.inicio)}</p>
          <BotonEnlace to="/gym/entrenamiento" icono={ArrowRight} tamano="grande">
            Continuar entrenamiento
          </BotonEnlace>
        </Card>
      ) : (
        <>
          <Propuestas
            hoy={propuestas.hoy}
            pendiente={propuestas.pendiente}
            ejerciciosDe={(routineId) => rutinas.find((r) => r.rutina.id === routineId)?.ejercicios ?? null}
            ocupado={ocupado}
            onEmpezar={(id) => void empezar(id)}
          />
          {siguientes.map((s) => (
            <TarjetaSigue key={s.routineId} siguiente={s} ocupado={ocupado} onEmpezar={(id) => void empezar(id)} />
          ))}
        </>
      )}

      {/* Los descansos confirmados son del gimnasio; las precargadas, de la modalidad que se ve */}
      {enGimnasio && <TarjetaDescansos cambios={cambiosDescanso} />}
      <TarjetaPrecargadas
        persona={persona}
        modalidad={modalidad}
        rutinas={rutinas.map((r) => ({ ...r.rutina, exerciseIds: r.exerciseIds }))}
      />

      <section aria-labelledby="titulo-rutinas">
        <div className="mb-2 flex items-center justify-between gap-2.5">
          <h2 id="titulo-rutinas" className="text-[17px] tracking-[-0.25px] text-texto">
            {enGimnasio ? 'Mis rutinas' : 'Mis rutinas de casa'}
          </h2>
          <span className="whitespace-nowrap text-[12px] text-texto-suave">
            {deModalidad.length === 1 ? '1 rutina' : `${deModalidad.length} rutinas`}
          </span>
        </div>
        {deModalidad.length > 0 ? (
          <Filas tarjetas>
            {deModalidad.map((r, i) => (
              <Fila
                key={r.rutina.id}
                tarjeta
                marca={String(i + 1).padStart(2, '0')}
                titulo={r.rutina.nombre}
                detalle={[
                  textoDias(r.rutina.dias) || (esDeCiclo(r.rutina.plantilla) ? 'Días variables' : ''),
                  textoEjercicios(r.ejercicios),
                  r.ultimaVez === null ? 'Sin hacer' : `Última vez: ${fechaCorta(r.ultimaVez)}`,
                ]
                  .filter((x) => x !== '')
                  .join(' · ')}
                a={`/gym/rutina/${r.rutina.id}`}
              />
            ))}
          </Filas>
        ) : (
          <p className="py-3 text-[13px] leading-relaxed text-texto-suave">
            {enGimnasio
              ? 'Todavía no tienes rutinas guardadas.'
              : 'Todavía no tienes rutinas de casa. Las del gimnasio siguen ahí, con su historial.'}
          </p>
        )}
      </section>

      {/* Las dos acciones de la mano; el resto, en Herramientas (reacomodo 0.11.0) */}
      <div className="grid grid-cols-2 gap-2">
        <Button
          icono={ListPlus}
          aria-label={enGimnasio ? 'Nueva rutina' : 'Nueva rutina de casa'}
          onClick={() => void nuevaRutina()}
        >
          Nueva rutina
        </Button>
        {enCurso === null && (
          <Button
            variante="secundario"
            icono={Play}
            aria-label={enGimnasio ? 'Entrenamiento libre' : 'Entrenamiento libre en casa'}
            disabled={ocupado}
            onClick={() => void empezar(null)}
          >
            Entrenamiento libre
          </Button>
        )}
      </div>

      <section aria-labelledby="titulo-herramientas">
        <h2 id="titulo-herramientas" className="mb-2 text-[17px] tracking-[-0.25px] text-texto">
          Herramientas
        </h2>
        <Filas tarjetas rejilla>
          <Fila tarjeta icono={History} titulo="Historial" a="/gym/historial" />
          <Fila tarjeta icono={Search} titulo="Ejercicios" a="/gym/ejercicios" />
          <Fila tarjeta icono={Calculator} titulo="Discos" a="/gym/discos" />
          <Fila tarjeta icono={FolderOpen} titulo="Traer rutina" a="/gym/importar-rutina" />
          {(persona === null || plantillaDe(persona, modalidad) !== null) && (
            <Fila tarjeta icono={Sparkles} titulo="Precargadas" a="/gym/plantilla" />
          )}
        </Filas>
      </section>
    </div>
  );
}

const conMayuscula = (t: string) => t.charAt(0).toLocaleUpperCase('es-MX') + t.slice(1);

/**
 * La sesión que toca y la que faltó, en una sola tarjeta (reacomodo 0.11.0):
 * la principal con su nombre grande y el botón al lado, y la otra en un
 * renglón abajo. Sin la de hoy, la que faltó toma el lugar principal.
 */
function Propuestas({
  hoy,
  pendiente,
  ejerciciosDe,
  ocupado,
  onEmpezar,
}: {
  hoy: SesionPropuesta | null;
  pendiente: SesionPropuesta | null;
  /** Cuántos ejercicios tiene esa rutina; null si ya no está. */
  ejerciciosDe: (routineId: string) => number | null;
  ocupado: boolean;
  onEmpezar: (routineId: string) => void;
}) {
  const principal = hoy ?? pendiente;
  if (principal === null) return null;
  const esDeHoy = hoy !== null;
  const secundaria = esDeHoy ? pendiente : null;
  const ejercicios = ejerciciosDe(principal.routineId);
  const detalle = [
    conMayuscula(nombreDia(principal.fecha)),
    ejercicios === null ? '' : textoEjercicios(ejercicios),
  ]
    .filter((x) => x !== '')
    .join(' · ');

  return (
    <Card>
      <div className="flex items-start justify-between gap-3">
        <span className="antetitulo">{esDeHoy ? 'Hoy toca' : 'Te faltó'}</span>
        {principal.hecha && <Pastilla>Hecha</Pastilla>}
      </div>
      <div className="mt-2.5 flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <h2 className="text-[20px] leading-tight tracking-[-0.5px] text-texto">{principal.nombre}</h2>
          <p className="mt-1 text-[13px] text-texto-suave">{detalle}</p>
        </div>
        {!principal.hecha && (
          <Button
            completo={false}
            icono={Play}
            disabled={ocupado}
            onClick={() => onEmpezar(principal.routineId)}
            className="shrink-0"
          >
            {esDeHoy ? 'Empezar' : 'Retomarla'}
          </Button>
        )}
      </div>
      {!esDeHoy && (
        <p className="mt-2.5 text-[12px] leading-relaxed text-texto-suave">
          Tocaba el {nombreDia(principal.fecha)} {formatearCorto(principal.fecha)}. Puedes retomarla o elegir otra.
        </p>
      )}
      {secundaria !== null && (
        <div className="mt-4 border-t border-borde pt-4">
          <span className="antetitulo">También pendiente</span>
          <div className="mt-2 flex items-center justify-between gap-3">
            <p className="min-w-0 flex-1 text-[14px] font-medium text-texto">
              {conMayuscula(nombreDia(secundaria.fecha))} · {secundaria.nombre}
            </p>
            <Button
              variante="secundario"
              completo={false}
              icono={Play}
              disabled={ocupado}
              onClick={() => onEmpezar(secundaria.routineId)}
              className="shrink-0"
            >
              Retomar
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
}

/** Encabezado reutilizable para las pantallas de fases futuras. */
export function AvisoDeFase({ ruta }: { ruta: string }) {
  const area = areaDe(ruta);
  if (area === undefined || disponible(area)) return null;
  return (
    <Card className="my-[18px]">
      <div className="flex items-start justify-between gap-3">
        <Emblema icono={area.icono} />
        <Pastilla>Fase {area.fase}</Pastilla>
      </div>
      <h2 className="mb-2 mt-3 text-[22px] leading-tight tracking-[-0.6px] text-texto">
        Llega en la Fase {area.fase}
      </h2>
      <p className="text-[13px] leading-relaxed text-texto-suave">{area.contenido}</p>
      <p className="mt-3 text-[12px] text-texto-suave">
        Vas en la Fase {FASE_ACTUAL}. Cada fase queda probada en el iPhone antes de empezar la
        siguiente.
      </p>
    </Card>
  );
}
